import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { openWhatsApp, openEmail, openCalendarEvent } from '@/lib/contact';
import { Badge } from '@/components/ui/badge';
import { FolderKanban, AlertCircle, Clock, IndianRupee, Users, MessageSquare, Calendar, ArrowUpRight, Plus, ChevronRight, Mail, Activity, Target, Truck, AlertTriangle, CreditCard } from 'lucide-react';

function formatINR(num) {
  if (!num) return '0';
  const n = Number(num);
  if (n >= 10000000) return `${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
  return n.toLocaleString('en-IN');
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-GB');
}

function formatDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  const today = new Date();
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  if (dt.toDateString() === today.toDateString()) return 'Today';
  if (dt.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}`;
}

const phases = ['Lead', 'Consultation', 'Design', 'Execution', 'Handover'];

function PhaseBar({ phase }) {
  const idx = phases.indexOf(phase);
  return (
    <div className="flex gap-1 items-center" data-testid="project-phase-bar">
      {phases.map((p, i) => (
        <div key={p} className={`h-1.5 w-8 rounded-full transition-colors ${i <= idx ? 'bg-[#3A8C8C]' : 'bg-[#E8E6E1]'}`} />
      ))}
    </div>
  );
}

const activityIcons = {
  task_status: Clock,
  task_created: Plus,
  meeting_added: Users,
  billing_update: IndianRupee,
};

const activityColors = {
  task_status: '#3A8C8C',
  task_created: '#7DA68A',
  meeting_added: '#C67D5B',
  billing_update: '#C67D5B',
};

const deadlineStatusColors = {
  overdue: { dot: 'bg-[#C4727F]', text: 'text-[#C4727F]', bg: 'bg-[#C4727F]/5', label: 'Overdue' },
  urgent: { dot: 'bg-[#C67D5B]', text: 'text-[#C67D5B]', bg: 'bg-[#C67D5B]/5', label: 'Urgent' },
  on_track: { dot: 'bg-[#7DA68A]', text: 'text-[#7DA68A]', bg: 'bg-[#7DA68A]/5', label: 'On Track' },
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, projRes, actRes, dlRes, pulseRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/projects'),
          api.get('/dashboard/recent-activity'),
          api.get('/dashboard/upcoming-deadlines'),
          api.get('/dashboard/procurement-pulse'),
        ]);
        setStats(statsRes.data);
        setProjects(projRes.data);
        setActivities(actRes.data);
        setDeadlines(dlRes.data);
        setPulse(pulseRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  const overviewCards = [
    { label: 'Active Projects', value: stats?.active_projects || 0, icon: FolderKanban, color: '#3A8C8C', trend: '+2' },
    { label: 'Pending Actions', value: stats?.pending_actions || 0, icon: AlertCircle, color: '#3A8C8C' },
    { label: 'Overdue Items', value: stats?.overdue_items || 0, icon: Clock, color: '#C4727F' },
    { label: 'Payments Due', value: `${formatINR(stats?.payments_due?.amount)}`, sub: `${stats?.payments_due?.count || 0} pending`, icon: IndianRupee, color: '#C67D5B' },
    { label: 'Leads This Month', value: stats?.leads_this_month || 0, sub: `${stats?.conversion_rate || 0}% conversion`, icon: Users, color: '#3A8C8C' },
    { label: "Today's Events", value: stats?.today_events || 0, icon: Calendar, color: '#7DA68A' },
  ];

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="dashboard-page">
      {/* Header */}
      <div className="section-heading-row flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-['Manrope'] font-bold" data-testid="dashboard-title">Studio</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">Overview of your design studio</p>
        </div>
        <button onClick={() => navigate('/projects')} data-testid="new-project-btn"
          className="bg-[#3A8C8C] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#2E7A7A] transition-colors flex items-center gap-2 active:scale-95">
          <Plus size={16} strokeWidth={1.5} /> New Project
        </button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4" data-testid="overview-cards">
        {overviewCards.map((card) => (
          <div key={card.label} className="glass-card rounded-2xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
            data-testid={`stat-card-${card.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between mb-3">
              <card.icon size={18} strokeWidth={1.5} style={{ color: card.color }} />
              {card.trend && <span className="text-xs text-[#7DA68A] flex items-center gap-0.5"><ArrowUpRight size={12} /> {card.trend}</span>}
            </div>
            <div className="text-xl font-semibold text-[#2C2C2E] font-['Manrope']">{card.value}</div>
            <div className="text-xs text-[#6B7280] mt-0.5">{card.label}</div>
            {card.sub && <div className="text-xs text-[#3A8C8C] mt-1">{card.sub}</div>}
          </div>
        ))}
      </div>

      {/* Project Cards */}
      <div>
        <div className="section-heading-row flex items-center justify-between mb-4">
          <h2 className="text-base font-['Manrope'] font-semibold">Active Projects</h2>
          <button onClick={() => navigate('/projects')} className="text-sm hover:underline flex items-center gap-1">View All <ChevronRight size={14} /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="project-cards">
          {projects.filter(p => p.status === 'Active').slice(0, 6).map(project => {
            const daysLeft = project.end_date ? Math.ceil((new Date(project.end_date) - Date.now()) / 86400000) : null;
            return (
              <div key={project.id} onClick={() => navigate(`/projects/${project.id}`)}
                className="glass-card rounded-2xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 cursor-pointer group"
                data-testid={`project-card-${project.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-[#2C2C2E] text-sm font-['Manrope'] group-hover:text-[#3A8C8C] transition-colors">{project.name}</h3>
                    <p className="text-xs text-[#6B7280] mt-0.5">{project.client_name}</p>
                  </div>
                  <Badge className="bg-[#E8E6E1] text-[#2C2C2E] text-[10px] font-normal">{project.project_type}</Badge>
                </div>
                <PhaseBar phase={project.phase} />
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-[#6B7280]">{project.phase}</span>
                  {daysLeft !== null && (
                    <span className={`text-xs font-medium ${daysLeft < 0 ? 'text-[#C4727F]' : daysLeft < 30 ? 'text-[#C67D5B]' : 'text-[#7DA68A]'}`}>
                      {daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` : `${daysLeft}d left`}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    className="p-1.5 rounded-lg hover:bg-[#E8E6E1] transition-colors" title="WhatsApp client"
                    onClick={e => { e.stopPropagation(); if (!openWhatsApp(project.client_phone, `Hi ${project.client_name},`)) toast.error('No client phone on this project'); }}
                  ><MessageSquare size={14} strokeWidth={1.5} className="text-[#6B7280]" /></button>
                  <button
                    className="p-1.5 rounded-lg hover:bg-[#E8E6E1] transition-colors" title="Email client"
                    onClick={e => { e.stopPropagation(); if (!openEmail(project.client_email, `Regarding ${project.name}`)) toast.error('No client email on this project'); }}
                  ><Mail size={14} strokeWidth={1.5} className="text-[#6B7280]" /></button>
                  <button
                    className="p-1.5 rounded-lg hover:bg-[#E8E6E1] transition-colors" title="Schedule meeting"
                    onClick={e => { e.stopPropagation(); openCalendarEvent({ title: `${project.name} — meeting`, details: `Client: ${project.client_name}` }); }}
                  ><Calendar size={14} strokeWidth={1.5} className="text-[#6B7280]" /></button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Procurement Pulse */}
      <ProcurementPulse pulse={pulse} navigate={navigate} />

      {/* Bottom Half: Recent Activity + Upcoming Deadlines */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Recent Activity Feed — 3 cols */}
        <div className="lg:col-span-3">
          <div className="glass-card rounded-2xl p-5" data-testid="recent-activity-feed">
            <div className="section-heading-row flex items-center gap-2 mb-4">
              <Activity size={16} strokeWidth={1.5} />
              <h3 className="text-sm font-semibold font-['Manrope']">Recent Activity</h3>
            </div>
            <div className="space-y-1 max-h-[400px] overflow-y-auto pr-1">
              {activities.length > 0 ? activities.map((act, i) => {
                const Icon = activityIcons[act.type] || Clock;
                const color = activityColors[act.type] || '#6B7280';
                return (
                  <div key={i} className="flex items-start gap-3 py-2.5 border-b border-[#E8E6E1] last:border-0" data-testid={`activity-item-${i}`}>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: `${color}10` }}>
                      <Icon size={13} strokeWidth={1.5} style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[#2C2C2E] leading-snug">{act.title}</p>
                      {act.detail && <p className="text-[10px] text-[#6B7280] mt-0.5 truncate">{act.detail}</p>}
                      <div className="flex items-center gap-2 mt-1">
                        {act.project_name && <span className="text-[9px] text-[#6B7280] bg-[#E8E6E1] px-1.5 py-0.5 rounded">{act.project_name}</span>}
                        {act.user_name && <span className="text-[9px] text-[#6B7280]">{act.user_name}</span>}
                      </div>
                    </div>
                    <span className="text-[9px] text-[#6B7280] shrink-0 mt-0.5">{timeAgo(act.timestamp)}</span>
                  </div>
                );
              }) : (
                <div className="text-center py-8">
                  <Activity size={24} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-2" />
                  <p className="text-xs text-[#6B7280]">No recent activity yet</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Upcoming Deadlines — 2 cols */}
        <div className="lg:col-span-2">
          <div className="glass-card rounded-2xl p-5" data-testid="upcoming-deadlines">
            <div className="section-heading-row flex items-center gap-2 mb-4">
              <Target size={16} strokeWidth={1.5} />
              <h3 className="text-sm font-semibold font-['Manrope']">Upcoming Deadlines</h3>
              <Badge className="bg-[#E8E6E1] text-[9px] ml-auto">Next 7 days</Badge>
            </div>
            <div className="space-y-1 max-h-[400px] overflow-y-auto pr-1">
              {deadlines.length > 0 ? deadlines.map((dl, i) => {
                const status = deadlineStatusColors[dl.priority_status] || deadlineStatusColors.on_track;
                return (
                  <div key={dl.id || i} className={`flex items-center gap-3 p-2.5 rounded-xl ${status.bg} transition-colors`}
                    data-testid={`deadline-item-${i}`}>
                    <div className={`w-2 h-2 rounded-full shrink-0 ${status.dot}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[#2C2C2E] truncate">{dl.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[9px] text-[#6B7280]">{dl.project_name}</span>
                        {dl.assigned_name && <span className="text-[9px] text-[#6B7280]">· {dl.assigned_name}</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className={`text-[10px] font-medium ${status.text}`}>{formatDate(dl.due_date)}</span>
                      <p className={`text-[9px] ${status.text}`}>
                        {dl.days_left < 0 ? `${Math.abs(dl.days_left)}d overdue` : dl.days_left === 0 ? 'Due today' : `${dl.days_left}d left`}
                      </p>
                    </div>
                  </div>
                );
              }) : (
                <div className="text-center py-8">
                  <Target size={24} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-2" />
                  <p className="text-xs text-[#6B7280]">No upcoming deadlines</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function ProcurementPulse({ pulse, navigate }) {
  if (!pulse) return null;
  const hasAny = (pulse.delayed_count || 0) > 0 || (pulse.overdue_bills_count || 0) > 0 || (pulse.delivered_unpaid_count || 0) > 0;
  return (
    <div className="glass-card rounded-2xl p-5" data-testid="procurement-pulse">
      <div className="section-heading-row flex items-center gap-2 mb-4">
        <Truck size={16} strokeWidth={1.5} />
        <h3 className="text-sm font-semibold font-['Manrope']">Procurement Pulse</h3>
        <Badge className="bg-[#E8E6E1] text-[9px] ml-auto">What to chase today</Badge>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="pulse-strip">
        <div className="rounded-xl p-3 bg-[#C4727F]/5 border border-[#C4727F]/15">
          <div className="flex items-center gap-1.5">
            <AlertTriangle size={11} className="text-[#C4727F]" />
            <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Delayed Orders</p>
          </div>
          <p className="text-lg font-bold text-[#2C2C2E] font-['Manrope'] mt-1" data-testid="pulse-delayed-count">{pulse.delayed_count || 0}</p>
          <p className="text-[10px] text-[#C4727F] mt-0.5">Rs {formatINR(pulse.at_risk_amount)} at risk</p>
        </div>
        <div className="rounded-xl p-3 bg-[#C67D5B]/5 border border-[#C67D5B]/15">
          <div className="flex items-center gap-1.5">
            <CreditCard size={11} className="text-[#C67D5B]" />
            <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Bills Overdue 30d+</p>
          </div>
          <p className="text-lg font-bold text-[#2C2C2E] font-['Manrope'] mt-1" data-testid="pulse-overdue-bills">{pulse.overdue_bills_count || 0}</p>
          <p className="text-[10px] text-[#C67D5B] mt-0.5">Rs {formatINR(pulse.overdue_bills_amount)} pending</p>
        </div>
        <div className="rounded-xl p-3 bg-[#3A8C8C]/5 border border-[#3A8C8C]/15">
          <div className="flex items-center gap-1.5">
            <Truck size={11} className="text-[#3A8C8C]" />
            <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Delivered · Unpaid</p>
          </div>
          <p className="text-lg font-bold text-[#2C2C2E] font-['Manrope'] mt-1">{pulse.delivered_unpaid_count || 0}</p>
          <p className="text-[10px] text-[#6B7280] mt-0.5">Need to settle</p>
        </div>
        <div className="rounded-xl p-3 bg-[#7DA68A]/5 border border-[#7DA68A]/15">
          <div className="flex items-center gap-1.5">
            <Users size={11} className="text-[#7DA68A]" />
            <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Watchlist Vendors</p>
          </div>
          <p className="text-lg font-bold text-[#2C2C2E] font-['Manrope'] mt-1">{(pulse.top_unreliable_vendors || []).length}</p>
          <p className="text-[10px] text-[#6B7280] mt-0.5">By avg delay</p>
        </div>
      </div>

      {!hasAny && (
        <div className="text-center py-6">
          <Truck size={26} strokeWidth={1.5} className="text-[#7DA68A] mx-auto mb-2" />
          <p className="text-xs text-[#6B7280]">All clear — no delayed orders or overdue bills</p>
        </div>
      )}

      {hasAny && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div data-testid="pulse-delayed-orders">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider">Delayed Orders</h4>
              <span className="text-[9px] text-[#6B7280]">Top {Math.min(5, (pulse.delayed_orders || []).length)}</span>
            </div>
            <div className="space-y-1.5">
              {(pulse.delayed_orders || []).slice(0, 5).map(o => (
                <button key={o.order_id} type="button"
                  onClick={() => navigate(`/projects/${o.project_id}`)}
                  className="w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg bg-white border border-[#E8E6E1] hover:border-[#C4727F]/40 hover:bg-[#FAFAF8] transition-colors"
                  data-testid={`pulse-delayed-${o.order_id}`}>
                  <div className="w-1.5 h-1.5 rounded-full bg-[#C4727F] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[#2C2C2E] truncate">{o.item_description || 'Untitled'}</p>
                    <p className="text-[9px] text-[#6B7280] truncate">{o.vendor_name} · {o.project_name}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] font-bold text-[#C4727F]">{o.days_delayed}d</p>
                    <p className="text-[9px] text-[#6B7280]">Rs {formatINR(o.order_amount)}</p>
                  </div>
                </button>
              ))}
              {(pulse.delayed_orders || []).length === 0 && (
                <p className="text-[10px] text-[#6B7280] py-3 text-center">No delayed orders</p>
              )}
            </div>
          </div>

          <div data-testid="pulse-overdue-bills-list">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider">Bills Overdue 30d+</h4>
              <button onClick={() => navigate('/reports')} className="text-[9px] text-[#3A8C8C] hover:underline flex items-center gap-0.5">
                Reports <ChevronRight size={9} />
              </button>
            </div>
            <div className="space-y-1.5">
              {(pulse.overdue_bills || []).slice(0, 5).map(b => (
                <button key={b.bill_id} type="button"
                  onClick={() => navigate(`/projects/${b.project_id}`)}
                  className="w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg bg-white border border-[#E8E6E1] hover:border-[#C67D5B]/40 hover:bg-[#FAFAF8] transition-colors"
                  data-testid={`pulse-overdue-bill-${b.bill_id}`}>
                  <div className="w-1.5 h-1.5 rounded-full bg-[#C67D5B] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[#2C2C2E] truncate">{b.vendor_name || 'No vendor'}{b.bill_number ? ` · #${b.bill_number}` : ''}</p>
                    <p className="text-[9px] text-[#6B7280] truncate">{b.project_name}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] font-bold text-[#C67D5B]">{b.days_overdue}d</p>
                    <p className="text-[9px] text-[#6B7280]">Rs {formatINR(b.pending_amount)}</p>
                  </div>
                </button>
              ))}
              {(pulse.overdue_bills || []).length === 0 && (
                <p className="text-[10px] text-[#6B7280] py-3 text-center">No bills overdue beyond 30 days</p>
              )}
            </div>
          </div>

          <div data-testid="pulse-unreliable-vendors">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider">Watchlist Vendors</h4>
              <span className="text-[9px] text-[#6B7280]">Avg delay</span>
            </div>
            <div className="space-y-1.5">
              {(pulse.top_unreliable_vendors || []).map(v => (
                <div key={v.vendor_name}
                  className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-white border border-[#E8E6E1]"
                  data-testid={`pulse-vendor-${v.vendor_name.replace(/\s+/g, '-')}`}>
                  <div className="w-7 h-7 rounded-full bg-[#3A8C8C]/10 flex items-center justify-center shrink-0">
                    <span className="text-[10px] font-semibold text-[#3A8C8C]">{(v.vendor_name || '?').slice(0, 2).toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[#2C2C2E] truncate">{v.vendor_name}</p>
                    <p className="text-[9px] text-[#6B7280]">{v.delayed_count}/{v.total_orders} delayed · {v.reliability_pct}% reliable</p>
                  </div>
                  <p className="text-[10px] font-bold text-[#C4727F] shrink-0">{v.avg_delay_days}d</p>
                </div>
              ))}
              {(pulse.top_unreliable_vendors || []).length === 0 && (
                <p className="text-[10px] text-[#6B7280] py-3 text-center">No vendors flagged yet</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
